/**
 * On offline → online, mark persisted query roots stale so active screens
 * refetch and inactive cached data refreshes on next visit.
 */
import { onlineManager } from "@tanstack/react-query";
import { awaitOnlineStatus } from "common/onlineManagerBridge";
import { queryClient } from "common/queryClient";
import { isPersistedQueryRoot } from "common/queryPersister";
import { useAppStore } from "stores";

let initialized = false;
let reconnectInvalidationPending = false;
let unsubscribeOnline: (() => void) | undefined;
let unsubscribeStore: (() => void) | undefined;

export function tryInvalidatePersistedQueriesOnReconnect(): void {
  if (!onlineManager.isOnline()) return;
  if (!useAppStore.getState().isAuthenticated) return;

  // Farm/app init uses fetchQuery for the same keys — invalidating mid-flight
  // can leave those promises hanging on the loading screen.
  if (useAppStore.getState().loading === "pending") {
    reconnectInvalidationPending = true;
    return;
  }

  reconnectInvalidationPending = false;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const root = query.queryKey[0];
      return typeof root === "string" && isPersistedQueryRoot(root);
    },
  });
}

/**
 * Subscribe to connectivity changes and invalidate persisted cache on reconnect.
 * Idempotent — safe to call once from App.tsx.
 */
export function initReconnectSync(): void {
  if (initialized) return;
  initialized = true;

  unsubscribeStore = useAppStore.subscribe((state, prevState) => {
    if (
      prevState.loading === "pending" &&
      state.loading === "idle" &&
      reconnectInvalidationPending
    ) {
      tryInvalidatePersistedQueriesOnReconnect();
    }
  });

  void awaitOnlineStatus().then(() => {
    let hasSeenOffline = !onlineManager.isOnline();

    unsubscribeOnline = onlineManager.subscribe((online) => {
      if (!online) {
        hasSeenOffline = true;
        return;
      }
      if (!hasSeenOffline) return;

      hasSeenOffline = false;
      tryInvalidatePersistedQueriesOnReconnect();
    });
  });
}

/** Test helper — reset module state between tests. */
export function resetReconnectSyncForTests(): void {
  unsubscribeOnline?.();
  unsubscribeOnline = undefined;
  unsubscribeStore?.();
  unsubscribeStore = undefined;
  initialized = false;
  reconnectInvalidationPending = false;
}

// Backwards-compatible export for tests
export const invalidatePersistedQueriesOnReconnect =
  tryInvalidatePersistedQueriesOnReconnect;
